import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import { PROFILE_DEVICES } from 'src/constants';
import { gen1CloudReq } from 'src/services/gen_1_reqs';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { localReq, postReq } from 'src/services/http';
import { zwaveLightSet } from 'src/services/xz_http_reqs';
import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
import { devices_status } from 'src/stores/device-runtime/status';
import {
	get_device_component_index,
	get_gen_2_component_from_category,
	get_local_ip,
	get_status_settings_component_path,
	get_z_wave_channel_structure_from_category,
	get_z_wave_ison,
	is_gen_2,
	is_gen_z,
	real_id,
} from './interface_helpers';
import type { RgbColor } from './light_color_math';
import { percent_from_value_range, value_from_percent_range } from './utilities';

const clampByte = (value: number): number => Math.min(255, Math.max(0, Math.round(value)));

const componentPath = (device: Device): string => get_status_settings_component_path(device) || '';

const zwavePath = (device: Device): string => get_z_wave_channel_structure_from_category(device);

const readZWaveBrightness = (block: Record<string, any> | undefined): number | undefined => {
	if (!block) return undefined;
	if (typeof block.brightness === 'number') return block.brightness;
	if (typeof block.currentValue === 'number') return block.currentValue;
	if (typeof block.value === 'number') return block.value;
	return undefined;
};

const patchZWaveLightBlock = (
	block: Record<string, any> | undefined,
	updates: { brightness?: number; on?: boolean },
): Record<string, any> => {
	const next = { ...(block || {}) };
	if (typeof updates.brightness === 'number') {
		next.brightness = updates.brightness;
		if (typeof next.value === 'number') next.value = updates.brightness;
		if (typeof next.currentValue === 'number') next.currentValue = updates.brightness;
	}
	if (typeof updates.on === 'boolean') {
		if (typeof next.output !== 'undefined') {
			next.output = updates.on;
		} else if (typeof next.on !== 'undefined') {
			next.on = updates.on;
		} else if (typeof next.currentValue === 'boolean') {
			next.currentValue = updates.on;
		} else {
			next.output = updates.on;
		}
		if (typeof next.ison !== 'undefined') next.ison = updates.on;
	}
	return next;
};

const isProfiled = (device: Device): boolean => PROFILE_DEVICES.includes(device.type);

const gen1Channel = (device: Device, status: Record<string, any> | undefined) => {
	const path = componentPath(device);
	return status?.[path]?.[device.channel];
};

export const readLightRgb = (
	device: Device,
	status: Record<string, any> | undefined,
): RgbColor | undefined => {
	if (!status) return undefined;
	if (is_gen_z(device)) {
		const rgb = status[zwavePath(device)]?.rgb;
		if (Array.isArray(rgb) && rgb.length >= 3) {
			return { r: rgb[0], g: rgb[1], b: rgb[2] };
		}
		return undefined;
	}
	const path = componentPath(device);
	if (!is_gen_2(device) && !isProfiled(device)) {
		const channel = gen1Channel(device, status);
		if (!channel) return undefined;
		return { r: channel.red, g: channel.green, b: channel.blue };
	}
	const rgb = status?.[path]?.rgb;
	if (Array.isArray(rgb) && rgb.length >= 3) {
		return { r: rgb[0], g: rgb[1], b: rgb[2] };
	}
	return undefined;
};

export const readLightCt = (
	device: Device,
	status: Record<string, any> | undefined,
): number | undefined => {
	if (!status) return undefined;
	if (is_gen_z(device)) {
		const ct = status[zwavePath(device)]?.ct;
		return typeof ct === 'number' ? ct : undefined;
	}
	const path = componentPath(device);
	if (is_gen_2(device)) {
		const ct = status?.[path]?.ct;
		return typeof ct === 'number' ? ct : undefined;
	}
	const temp = gen1Channel(device, status)?.temp;
	return typeof temp === 'number' ? temp : undefined;
};

export const readLightChannel = (
	device: Device,
	status: Record<string, any> | undefined,
	prop: string,
	needsRanging = false,
): number | undefined => {
	if (!status || !prop) return undefined;
	if (is_gen_z(device)) {
		const block = status[zwavePath(device)];
		const raw = prop === 'brightness' ? readZWaveBrightness(block) : block?.[prop];
		if (typeof raw !== 'number') return undefined;
		return needsRanging ? percent_from_value_range(raw, [0, 255]) : raw;
	}
	const path = componentPath(device);
	const raw = is_gen_2(device) ? status?.[path]?.[prop] : gen1Channel(device, status)?.[prop];
	if (typeof raw !== 'number') return undefined;
	return needsRanging ? percent_from_value_range(raw, [0, 255]) : raw;
};

export const readLightOn = (device: Device, status: Record<string, any> | undefined): boolean => {
	if (!status) return false;
	if (is_gen_z(device)) {
		return get_z_wave_ison(status[zwavePath(device)]);
	}
	const path = componentPath(device);
	if (is_gen_2(device)) {
		const block = status?.[path];
		if (typeof block?.output === 'boolean') return block.output;
		if (typeof block?.on === 'boolean') return block.on;
		return false;
	}
	return Boolean(gen1Channel(device, status)?.ison);
};

/**
 * RGB / RGBW / RGBCCT.Set reject rgb- or ct-only payloads. Keep the live
 * output so a color change is accepted without toggling the light.
 */
const gen2LightSetKeepAlive = (
	device: Device,
	status: Record<string, any>,
): { on: boolean; brightness?: number } => {
	const on = readLightOn(device, status);
	const brightness = readLightChannel(device, status, 'brightness');
	if (typeof brightness !== 'number') return { on };
	return { on, brightness: Math.round(brightness) };
};

export const setLightRgb = async (
	device: Device,
	status: Record<string, any>,
	rgb: RgbColor,
): Promise<boolean> => {
	const path = componentPath(device);
	const value = {
		r: clampByte(rgb.r),
		g: clampByte(rgb.g),
		b: clampByte(rgb.b),
	};
	const id = real_id(device.id);
	const localIp = get_local_ip(id);

	if (!is_gen_2(device) && !isProfiled(device)) {
		if (status[path]?.[device.channel]) {
			status[path][device.channel].red = value.r;
			status[path][device.channel].green = value.g;
			status[path][device.channel].blue = value.b;
		}
	} else if (status[path]?.rgb) {
		status[path].rgb[0] = value.r;
		status[path].rgb[1] = value.g;
		status[path].rgb[2] = value.b;
	}

	devices_status.update_device_status({ id, msg: status });

	const colorData = { red: value.r, green: value.g, blue: value.b };
	let res;

	if (!is_gen_2(device)) {
		if (localIp) {
			const mode = status[path]?.[device.channel]?.mode || 'color';
			res = await localReq(
				device,
				`${localIp}/${mode}/${device.channel}`,
				'post',
				colorData,
				9000,
				true,
			);
			if (!res?.isok && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
				res = await gen1CloudReq(device, device.category, colorData);
			}
		} else {
			res = await gen1CloudReq(device, device.category, colorData);
		}
	} else {
		res = await gen2Request(device, `${get_gen_2_component_from_category(device)}.set`, {
			id: get_device_component_index(device),
			rgb: [value.r, value.g, value.b],
			...gen2LightSetKeepAlive(device, status),
		});
	}

	return Boolean(res?.isok);
};

export const setLightCt = async (
	device: Device,
	status: Record<string, any>,
	ct: number,
): Promise<boolean> => {
	const path = componentPath(device);
	const id = real_id(device.id);
	const localIp = get_local_ip(device.id);
	const value = Math.round(ct);

	if (is_gen_2(device)) {
		if (status[path]) status[path].ct = value;
	} else if (status[path]?.[device.channel]) {
		status[path][device.channel].temp = value;
	}

	devices_status.update_device_status({ id, msg: status });

	let res;
	if (is_gen_2(device)) {
		res = await gen2Request(device, `${get_gen_2_component_from_category(device)}.set`, {
			id: get_device_component_index(device),
			ct: value,
			...gen2LightSetKeepAlive(device, status),
		});
	} else if (localIp) {
		res = await localReq(
			device,
			`${localIp}/light/${device.channel}`,
			'post',
			{ temp: value },
			6000,
			true,
		);
	} else {
		res = await gen1CloudReq(device, device.category, { temp: value });
	}

	return Boolean(res?.isok);
};

export const setLightChannel = async (
	device: Device,
	status: Record<string, any>,
	prop: string,
	uiValue: number,
	needsRanging = false,
): Promise<boolean> => {
	const path = componentPath(device);
	const id = real_id(device.id);
	const localIp = get_local_ip(device.id);
	const _value = needsRanging ? value_from_percent_range(uiValue, [0, 255]) : uiValue;

	if (is_gen_z(device)) {
		const zwPath = zwavePath(device);
		const nextOn = prop === 'brightness' ? _value > 0 : undefined;
		status[zwPath] = patchZWaveLightBlock(status[zwPath], {
			...(prop === 'brightness' ? { brightness: _value } : {}),
			...(typeof nextOn === 'boolean' ? { on: nextOn } : {}),
		});
		if (prop !== 'brightness' && status[zwPath]) {
			status[zwPath][prop] = _value;
		}
		const res = await zwaveLightSet(device, {
			...(prop === 'brightness' ? { brightness: _value, on: nextOn } : {}),
		});
		if (res?.isok) {
			devices_status.update_device_status({ id, msg: status });
		}
		return Boolean(res?.isok);
	}

	if (is_gen_2(device)) {
		if (status[path]) status[path][prop] = _value;
	} else if (status[path]?.[device.channel]) {
		status[path][device.channel][prop] = _value;
	}

	let res;
	if (is_gen_2(device)) {
		res = await gen2Request(device, `${get_gen_2_component_from_category(device)}.set`, {
			id: get_device_component_index(device),
			...gen2LightSetKeepAlive(device, status),
			[prop]: _value,
		});
	} else if (localIp) {
		const data: Record<string, unknown> = { [prop]: _value };
		if (prop === 'brightness') {
			data.turn = status[path]?.[device.channel]?.ison ? 'on' : 'off';
		}
		res = await localReq(
			device,
			`${localIp}/${device.category}/${device.channel}`,
			'post',
			data,
			9000,
			true,
		);
		if (!res?.isok && typeof get(cloud_disabled_devices)[device.id] === 'undefined') {
			res = await postReq(`/device/${device.category}/control`, data);
		}
	} else {
		const props: Record<string, unknown> = { [prop]: _value };
		if (prop === 'brightness') {
			props.turn = status[path]?.[device.channel]?.ison ? 'on' : 'off';
		}
		res = await gen1CloudReq(device, device.category, props);
	}

	if (res?.isok) {
		devices_status.update_device_status({ id, msg: status });
	}
	return Boolean(res?.isok);
};
